// components/slides/SlidesReviewLauncher.tsx
//
// One-click "run the review" pill, shown directly above the chat composer while
// a Slides session is open. Before this, starting a review meant typing free
// text and hoping it hit the doctrine's vocabulary - there is no
// SLIDES_START_REVIEW command; the whole multi-pass run is triggered by prose.
//
// The guidelines are NOT sent with the click. Everything binding is already
// injected server-side on every slides turn: the deck's guideline chapter (from
// deck.guidelineKey, via slidesKnowledgeBase) and the organisation's editable
// Slides doctrine - the TOOL/SLIDES page in the Doctrine tab, which names the
// RDM Guidelines PDF as the source of truth (lib/prompt/contextStack.ts →
// app/api/ai/assist/route.ts). So the button always runs against whatever is
// loaded in the doctrine right now.
//
// The click does NOT put a prompt in the chat. It used to write REVIEW_PROMPT
// into the composer and submit it, so the user's own transcript opened with a
// paragraph of instructions they never typed. askAssistant already has
// `skipUserMessage` for exactly this: the directive still reaches the agent,
// it just is not rendered as if the user had written it.

'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { isScriptExecuting } from '@/kernel/useCommands';
import { checkHostedReview, launchSlidesReview, pressHostedReview, stopHostedReview, useSlidesStore, REVIEW_PLACED_TYPES } from './slidesStore';
import { guidelineLabel } from './slidesUi';
import { WESTFIELD_GUIDELINES_LABEL } from '#lib/slides/westfieldGuidelines';
import { SlidesReviewStatus } from './SlidesReviewStatus';
import { SlidesProposalStatus } from './SlidesProposalStatus';
import { reviewHasCompleteResult, reviewLanguageFromPreference, reviewProgress } from '#lib/slides/reviewStatus';

/**
 * Phrased in the review doctrine's own vocabulary (slidesKnowledgeBase §4 "THE
 * REVIEW PROTOCOL") so the agent enters Pass 0 and auto-continues, rather than
 * answering conversationally and waiting.
 */
/**
 * The counter-proposal directive.
 *
 * Deliberately prose, exactly like REVIEW_PROMPT: SLIDES_PROPOSE_VISUAL needs a
 * page, a crop region in page points and the corrections to apply, and only the
 * agent can supply those - it has to pick the sheet carrying the render, frame
 * the storefront on the coordinate grid, and draw the changes from findings it
 * already made. A button cannot know any of that. What the button removes is
 * having to remember the vocabulary.
 *
 * The ranking of page kinds below is the client's (URW, 01/09/2026) and it is
 * written in THREE places that must agree - here, slidesKnowledgeBase §3 and the
 * SLIDES_PROPOSE_VISUAL description in slidesCommands. Change one, change all.
 */
const PROPOSAL_PROMPT =
  'Generate a fresh counter-proposal for this deck now with SLIDES_PROPOSE_VISUAL, even if older counter-proposal sheets already exist. ' +
  'Use the saved brand research and vetted reference images when available, with active guidelines taking priority. ' +
  'Preserve the existing sheets. This request is only for new proposal sheets: do not change review verdicts, the stamp or synthesis, and do not finalize the review. ' +
  'Pick the page carrying the storefront render, preferring a 3D view or perspective over a flat ' +
  'facade elevation - take the elevation only when the deck carries no 3D view - and never a plan, ' +
  'a section or a location map. Frame the render generously in page points, and pass ' +
  'the corrections from the findings you have already made, each one physical, specific and short ' +
  'enough to be written by hand on the sheet. Then report both sheets: say plainly if either was not ' +
  'approved by the critic, and quote what it still objects to.';

const CONTEXT_PROMPT =
  'Ask one short question in the user\'s language about any project context or constraints to consider before reviewing this deck. ' +
  'Only ask the question, then wait. Do not start the review, run commands or automatically continue. ' +
  'After context-only replies, acknowledge them and wait. If the user asks to start or continue the review, call SLIDES_LAUNCH_REVIEW and stop; never review inline.';

// What "the review" is, on the page: the callouts and the verdict stamp,
// REGARDLESS of who authored them.
//
// Selecting them by `origin === 'agent'` was the obvious idea and it silently
// did NOTHING - measured on a real deck, 199 elements and not one of them
// tagged agent, because provenance is not reliably persisted on decks made
// before that field, or on elements that came back through an edit. A button
// that quietly removes nothing is worse than no button.
//
// Type is the durable signal. Filtering on origin cannot work either way
// round: addElement DEFAULTS origin to 'user' (slidesStore), so on a legacy
// deck every element reads as user-authored and an origin filter removes
// either nothing or everything. So this removes hand-drawn notes too, the
// tooltip says so plainly, and the confirm step is what makes that safe.
// Drawings, text boxes and images are left alone.

// ChatGPT's suggestion chips: outlined on the page, not grey tiles.
export const PILL_CLASS =
  'h-8 shrink-0 whitespace-nowrap rounded-full border border-[#2A2A2A] bg-transparent px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40';
export const PILL_STYLE = { fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif', fontSize: '13px' };

export const SlidesReviewLauncher: React.FC = () => {
  const { kernel } = useKernel();

  // A chat belongs to Slides for life via the session's creationModule - there
  // is no route segment or active-module value to read (Portal.tsx does the
  // same test to decide the whole Slides surface).
  const isSlidesSession = useKernelStore((s) => {
    const id = s.activeChatSessionId;
    if (!id) return false;
    return s.inventory.ChatSession.registry[id]?.creationModule === 'slides';
  });
  const deck = useSlidesStore((s) => (s.activeProjectId ? s.decks[s.activeProjectId] ?? null : null));
  const deckProjectId = deck?.projectId;
  const hostedRunner = useSlidesStore((s) => s.hostedRunner);
  const hostedRequest = useSlidesStore((s) => s.hostedReviewRequest);
  const watchingHosted = useSlidesStore((s) => !!deckProjectId && s.hostedReview?.projectId === deckProjectId);

  // A turn already in flight: askAssistant refuses one anyway, so don't offer it.
  const isReviewRunning = useSlidesStore((s) => {
    const pid = s.activeProjectId;
    const doc = pid ? s.decks[pid]?.doc : undefined;
    return [doc?.reviewRun, doc?.proposalRun].some(run => run && s.getAgentRun(run.sessionId)?.id === run.id);
  });
  const isQueuing = useKernelStore((s) => {
    const id = s.activeChatSessionId;
    const runtime = id ? s.inventory.ChatSession.registry[id]?.runtime : undefined;
    const controller = runtime?.abortController;
    // A stopped turn still finishes the command it was running, and askAssistant drops a click
    // made before that: the actions wait for it.
    const scriptId = typeof runtime?.runningScriptId === 'string' ? runtime.runningScriptId.trim() : '';
    return s.isQueuingChatMessage || !!(controller && !controller.signal.aborted) || (!!scriptId && isScriptExecuting(scriptId));
  }) || isReviewRunning || watchingHosted;
  const activeChatSessionId = useKernelStore((s) => s.activeChatSessionId);
  const savedLanguage = useKernelStore((s) => reviewLanguageFromPreference(s.userPreferences?.language));
  const reviewLanguage = useSlidesStore((s) => {
    const pid = s.activeProjectId;
    return pid ? s.runtime[pid]?.reviewLanguage ?? s.decks[pid]?.doc.review?.language : undefined;
  }) ?? savedLanguage;

  // The worker's browser presses the review once its deck and conversation are open.
  useEffect(() => {
    const k = kernel.current;
    // Read from the store, not the render: an effect run twice (React's development check) must press it once,
    // or the second press cancels the brand research the first one started.
    const request = useSlidesStore.getState().hostedReviewRequest;
    if (!request || !k || request.projectId !== deckProjectId || request.sessionId !== activeChatSessionId) return;
    useSlidesStore.setState({ hostedReviewRequest: null });
    void pressHostedReview(k, request);
  }, [hostedRequest, deckProjectId, activeChatSessionId]);

  // A review the worker runs: this tab looks every few seconds until it ends, its own copy of the deck never
  // updating otherwise. One look on opening a deck finds a review started from another tab.
  useEffect(() => {
    if (!deckProjectId || hostedRunner) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const look = async () => {
      const active = await checkHostedReview(deckProjectId);
      if (!cancelled && active) timer = setTimeout(look, 5_000);
    };
    void look();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [deckProjectId, hostedRunner, watchingHosted]);

  const stopHosted = useCallback(() => {
    if (deckProjectId) void stopHostedReview(deckProjectId);
  }, [deckProjectId]);

  const launch = useCallback((mode: 'start' | 'restart' | 'resume') => {
    const k = kernel.current;
    if (k && activeChatSessionId) launchSlidesReview(k, activeChatSessionId, mode);
  }, [kernel, activeChatSessionId]);

  const chooseLanguage = useCallback((language: 'fr' | 'en') => {
    const store = useSlidesStore.getState();
    const projectId = store.activeProjectId;
    if (!projectId || store.runtime[projectId]?.reviewLanguage === language) return;
    store.patchRuntime(projectId, { reviewLanguage: language });
    kernel.current?.logUserAction?.('Slides Review Language Chosen', { projectId, language });
  }, [kernel]);

  const giveContext = useCallback(() => {
    useSlidesStore.getState().cancelBrandResearch();
    const k = kernel.current;
    if (!k || !activeChatSessionId) return;
    void k.askAssistant(activeChatSessionId, CONTEXT_PROMPT, undefined, undefined, {
      agentMode: 'slides',
      skipUserMessage: true,
      showThinking: true,
      ignoreScripts: true,
    });
    k.logUserAction?.('Slides Context Requested', { guideline: deck?.guidelineKey ?? null });
  }, [kernel, deck, activeChatSessionId]);

  const proposeVisual = useCallback(() => {
    useSlidesStore.getState().cancelBrandResearch();
    const k = kernel.current;
    if (!k || !activeChatSessionId) return;
    void k.askAssistant(activeChatSessionId, PROPOSAL_PROMPT, undefined, undefined, {
      agentMode: 'slides',
      skipUserMessage: true,
      showThinking: true,
      slidesProposal: true,
    });
    k.logUserAction?.('Slides Counter Proposal Launched', { guideline: deck?.guidelineKey ?? null });
  }, [kernel, deck, activeChatSessionId]);

  const agentNoteIds = deck
    ? deck.doc.pages.map((p) => ({
        pageId: p.id,
        ids: p.elements.filter((e) => REVIEW_PLACED_TYPES.has(e.type)).map((e) => e.id),
      }))
    : [];
  const agentNoteCount = agentNoteIds.reduce((n, p) => n + p.ids.length, 0);
  const hasReviewState = !!deck?.doc.review && Object.keys(deck.doc.review.points ?? {}).length > 0;
  const hasReview = agentNoteCount > 0 || hasReviewState;
  // One rule, read off the deck (owner, 2026-10-06): Launch only at zero, Continue for anything
  // started and unfinished, however it ended (stopped, failed, a reload mid-run). Offering only
  // "Review" there restarted from scratch and wiped the verdicts: 75% fell back to 15%.
  const reviewStarted = !!deck && (reviewProgress(deck).percent > 0 || agentNoteCount > 0);
  const reviewDone = !!deck && reviewHasCompleteResult(deck);

  // null = idle, otherwise which destructive button is armed.
  const [confirmation, setConfirmation] = useState<{ action: 'clear' | 'again'; projectId: string; sessionId: string | null } | null>(null);
  const confirmClear = confirmation?.projectId === deck?.projectId && confirmation?.sessionId === activeChatSessionId
    ? confirmation?.action : null;

  const clearReview = useCallback(() => {
    const k = kernel.current;
    if (!deck) return;
    const removed = useSlidesStore.getState().clearReview(deck.projectId);
    k?.logUserAction?.('Slides Review Cleared', { removed });
  }, [kernel, deck]);

  // Two-step, no modal. Deleting a whole review is destructive and easy to hit
  // by accident next to the composer; arming it costs one click and makes the
  // intent explicit.
  const handleClearClick = useCallback(() => {
    if (confirmClear !== 'clear') {
      if (deck) setConfirmation({ action: 'clear', projectId: deck.projectId, sessionId: activeChatSessionId });
      return;
    }
    setConfirmation(null);
    clearReview();
  }, [confirmClear, clearReview, deck, activeChatSessionId]);

  const handleAgainClick = useCallback(() => {
    if (confirmClear !== 'again') {
      if (deck) setConfirmation({ action: 'again', projectId: deck.projectId, sessionId: activeChatSessionId });
      return;
    }
    setConfirmation(null);
    launch('restart');
  }, [confirmClear, launch, deck, activeChatSessionId]);

  // A presentation is built, not reviewed: no review to launch on it.
  if (!isSlidesSession || !deck || deck.doc.kind === 'presentation') return null;

  const guideline = guidelineLabel(deck.guidelineKey);

  // Launch shows whenever the deck has no review on it. It used to also require
  // an empty chat, on the reasoning that the pill is clutter once a conversation
  // has started, and that left a dead end: a review that RAN and placed nothing
  // leaves messages in the chat and no annotations on the deck, so `showLaunch`
  // was false and `hasReview` was false, the component returned null, and the
  // user was looking at a deck with no way to start a review at all. That is the
  // state the FEVER-ROSNY deck was found in, and it reads exactly like "the
  // launch review button does not work".
  //
  // Having no review is the condition that matters; whether anything has been
  // said in the chat is not. The clear/redo pair remains the opposite case, so
  // one of the two is always offered.
  const showLaunch = !reviewStarted;

  return (
    <>
    <SlidesReviewStatus />
    <SlidesProposalStatus />
    <div data-slides-tour-project={deck.projectId} data-slides-tour-session={activeChatSessionId ?? undefined} className="mb-2 flex min-w-0 flex-nowrap items-center justify-start gap-1 overflow-x-auto">
      {showLaunch && (
        <>
          <div role="group" aria-label="Review language" className="flex h-8 shrink-0 items-center rounded-full border border-[#2A2A2A] p-0.5"
            title={reviewLanguage ? 'Language the review is written in. Applies to this deck.' : 'Follows the language of the dossier until you pick one. Applies to this deck.'}>
            {(['fr', 'en'] as const).map((language) => (
              <button key={language} type="button" aria-pressed={reviewLanguage === language} disabled={isQueuing}
                onClick={() => chooseLanguage(language)} style={PILL_STYLE}
                className={`h-full rounded-full px-2.5 transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${reviewLanguage === language ? 'bg-[#2E2E2E] text-[#ECECEC]' : 'text-[#8E8E8E] hover:text-[#ECECEC]'}`}>
                {language === 'fr' ? 'FR' : 'EN'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={giveContext}
            data-slides-tour="context"
            disabled={isQueuing}
            title="Add project context in the chat before launching the review."
            className={PILL_CLASS}
            style={PILL_STYLE}
          >
            Give context
          </button>
          <button
            type="button"
            onClick={() => launch('start')}
            data-slides-tour="review"
            disabled={isQueuing}
            title={`Full ${guideline} review against ${WESTFIELD_GUIDELINES_LABEL}, plus your organisation's Slides doctrine as currently loaded. The first review of a deck researches the brand on the web first.`}
            className={PILL_CLASS}
            style={PILL_STYLE}
          >
            Launch review
          </button>
        </>
      )}
      {watchingHosted && (
        <button type="button" onClick={stopHosted} className={PILL_CLASS} style={PILL_STYLE}
          title="Stops the review Fater is running for this deck. Everything placed so far is kept.">
          Stop review
        </button>
      )}
      {reviewStarted && !reviewDone && !isReviewRunning && !watchingHosted && (
        <button
          type="button"
          onClick={() => launch('resume')}
          data-slides-tour="review"
          disabled={isQueuing}
          title="Continues this review: keeps every label, verdict and callout already placed and works only what is still open."
          className={PILL_CLASS}
          style={PILL_STYLE}
        >
          Continue review
        </button>
      )}
      {hasReview && (
        <>
          <button
            type="button"
            onClick={handleClearClick}
            data-slides-tour="clear"
            aria-label={confirmClear === 'clear' ? 'Confirm? Clear review from all pages' : 'Clear review from all pages'}
            disabled={isQueuing}
            title={`Deletes ALL ${agentNoteCount} callout(s) and stamp(s) on every page of this deck and empties the verdict card. Notes you drew by hand go too: on decks made before provenance was recorded there is no way to tell yours from the agent's, so this removes them all.`}
            className={PILL_CLASS}
            style={PILL_STYLE}
          >
            {confirmClear === 'clear' ? 'Confirm?' : 'Clear review'}
          </button>
          <button
            type="button"
            onClick={proposeVisual}
            data-slides-tour="proposal"
            disabled={isQueuing}
            title="Built from the callouts as they are on the deck right now, so finish reworking them first: reword, move or delete, then press this. Two sheets: your remarks drawn by hand on the tenant's own render, then the corrected design generated from that mark-up. Each is judged by an independent critic and regenerated up to three times, so it takes a couple of minutes and spends credits on several image generations."
            className={PILL_CLASS}
            style={PILL_STYLE}
          >
            Counter-proposal
          </button>
          {reviewDone && <button
            type="button"
            onClick={handleAgainClick}
            data-slides-tour="again"
            aria-label={confirmClear === 'again' ? 'Confirm? Review again' : 'Review again'}
            disabled={isQueuing}
            title="Deletes the current review and immediately starts a fresh one on the same guidelines."
            className={PILL_CLASS}
            style={PILL_STYLE}
          >
            {confirmClear === 'again' ? 'Confirm?' : 'Review'}
          </button>}
        </>
      )}
    </div>
    </>
  );
};
